// ============================================================================
// ROLE HOOKS
// ============================================================================
// React hooks wrapping roleService for the Role Master screen, form modal and
// view modal.

import { useState, useEffect, useCallback } from 'react';
import { roleService } from 'api/services';
import type {
  ID,
  RoleResponse,
  CreateRoleRequest,
  UpdateRoleRequest,
  RoleExistenceValidation,
  RoleScopeAssignmentResponse,
  UpdateRoleScopeDefaultRequest,
  KeycloakRoleResponse,
} from 'api/types';

/** Options accepted by every mutation hook in this module. */
interface MutationOptions<T = void> {
  onSuccess?: (result: T) => void;
  onError?: (error: Error) => void;
}

// ============================================================================
// LIST
// ============================================================================

/**
 * Fetch roles. When `roleType` or `appId` is supplied the dedicated
 * server-side endpoints are used instead of the full list.
 */
export const useRoles = (params?: { roleType?: string; appId?: ID }) => {
  const [data, setData] = useState<RoleResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const roleType = params?.roleType;
  const appId = params?.appId;

  const fetchRoles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = roleType
        ? await roleService.getByType(roleType)
        : appId
          ? await roleService.getByAppId(appId)
          : await roleService.getAll();
      setData(response.data ?? []);
    } catch (err) {
      setError(err as Error);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [roleType, appId]);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  return { data, loading, error, refetch: fetchRoles };
};

// ============================================================================
// SINGLE ROLE
// ============================================================================

/** Fetch one role by ID. Pass null to skip fetching. */
export const useRole = (id: ID | null) => {
  const [data, setData] = useState<RoleResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchRole = useCallback(async () => {
    if (!id) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await roleService.getById(id);
      setData(response.success ? response.data ?? null : null);
    } catch (err) {
      setError(err as Error);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchRole();
  }, [fetchRole]);

  return { data, loading, error, refetch: fetchRole };
};

// ============================================================================
// MUTATIONS
// ============================================================================

export const useCreateRole = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const createRole = useCallback(
    async (data: CreateRoleRequest, options?: MutationOptions<RoleResponse>) => {
      setLoading(true);
      setError(null);
      try {
        const response = await roleService.create(data);
        if (!response.success) throw new Error(response.message || 'Failed to create role');
        options?.onSuccess?.(response.data as RoleResponse);
        return response.data;
      } catch (err) {
        const error = err as Error;
        setError(error);
        options?.onError?.(error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { createRole, loading, error };
};

export const useUpdateRole = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const updateRole = useCallback(
    async (id: ID, data: UpdateRoleRequest, options?: MutationOptions<RoleResponse>) => {
      setLoading(true);
      setError(null);
      try {
        const response = await roleService.update(id, data);
        if (!response.success) throw new Error(response.message || 'Failed to update role');
        options?.onSuccess?.(response.data as RoleResponse);
        return response.data;
      } catch (err) {
        const error = err as Error;
        setError(error);
        options?.onError?.(error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { updateRole, loading, error };
};

export const useDeleteRole = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const deleteRole = useCallback(async (id: ID, options?: MutationOptions) => {
    setLoading(true);
    setError(null);
    try {
      const response = await roleService.delete(id);
      if (!response.success) throw new Error(response.message || 'Failed to delete role');
      options?.onSuccess?.();
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  return { deleteRole, loading, error };
};

// ============================================================================
// KEYCLOAK SYNC
// ============================================================================

/**
 * Keycloak operations: push one role, push all roles, import from Keycloak and
 * validate a single role code. All share one loading flag since the screen
 * runs them one at a time from the sync dialog.
 */
export const useRoleKeycloakSync = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const run = useCallback(async <T,>(op: () => Promise<T>, options?: MutationOptions<T>) => {
    setLoading(true);
    setError(null);
    try {
      const result = await op();
      options?.onSuccess?.(result);
      return result;
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const syncToKeycloak = useCallback(
    (id: ID, realmName: string, clientId: string, options?: MutationOptions<RoleResponse | undefined>) =>
      run(async () => {
        const response = await roleService.syncToKeycloak(id, realmName, clientId);
        if (!response.success) throw new Error(response.message || 'Failed to sync role to Keycloak');
        return response.data;
      }, options),
    [run],
  );

  const syncAllToKeycloak = useCallback(
    (realmName: string, clientId: string, options?: MutationOptions<RoleResponse[] | undefined>) =>
      run(async () => {
        const response = await roleService.syncAllToKeycloak(realmName, clientId);
        if (!response.success) throw new Error(response.message || 'Failed to sync roles to Keycloak');
        return response.data;
      }, options),
    [run],
  );

  const syncFromKeycloak = useCallback(
    (realmName: string, clientId: string, appId?: ID, options?: MutationOptions<boolean | undefined>) =>
      run(async () => {
        const response = await roleService.syncFromKeycloak(realmName, clientId, appId);
        if (!response.success) throw new Error(response.message || 'Failed to import roles from Keycloak');
        return response.data;
      }, options),
    [run],
  );

  const validateRole = useCallback(
    (roleCode: string, realmName: string, clientId?: string, options?: MutationOptions<RoleExistenceValidation | undefined>) =>
      run(async () => {
        const response = await roleService.validate(roleCode, realmName, clientId);
        if (!response.success) throw new Error(response.message || 'Failed to validate role');
        return response.data;
      }, options),
    [run],
  );

  return { syncToKeycloak, syncAllToKeycloak, syncFromKeycloak, validateRole, loading, error };
};

/**
 * Read-only listing of the CLIENT roles Keycloak holds for a realm/client.
 * Loads automatically whenever both values are present (debounced, so typing in
 * the fields by hand does not fire a request per keystroke) and clears when
 * either is missing. Kept separate from useRoleKeycloakSync so this background
 * read never drives the dialog's blocking "Contacting Keycloak..." overlay.
 */
export const useKeycloakClientRoles = (realmName?: string, clientId?: string) => {
  const [data, setData] = useState<KeycloakRoleResponse[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [message, setMessage] = useState('');
  /** Bumped by refetch() to re-run the effect after a sync changes Keycloak. */
  const [reloadToken, setReloadToken] = useState(0);

  const realm = (realmName || '').trim();
  const client = (clientId || '').trim();

  const refetch = useCallback(() => setReloadToken(t => t + 1), []);

  useEffect(() => {
    if (!realm || !client) {
      setData(null);
      setMessage('');
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setMessage('');

    const timer = setTimeout(async () => {
      try {
        const response = await roleService.getKeycloakRoles(realm, client);
        if (cancelled) return;
        if (response.success) {
          setData(response.data ?? []);
        } else {
          setData(null);
          setMessage(response.message || 'Could not read roles from Keycloak.');
        }
      } catch (err: any) {
        if (cancelled) return;
        setError(err as Error);
        setData(null);
        setMessage(
          err?.response?.data?.message ||
          `No Keycloak client "${client}" found in realm "${realm}".`,
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 400);

    return () => { cancelled = true; clearTimeout(timer); };
  }, [realm, client, reloadToken]);

  return { data, loading, error, message, refetch };
};

// ============================================================================
// SCOPE ASSIGNMENTS
// ============================================================================

/**
 * Scope assignments for a role (entities / business units / financial years).
 * These endpoints are tenant-scoped: the API returns an error when the caller
 * has no tenant context, which the consumer surfaces as an inline message.
 */
export const useRoleScopeAssignments = (roleId: ID | null) => {
  const [data, setData] = useState<RoleScopeAssignmentResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [message, setMessage] = useState<string>('');

  const fetchAssignments = useCallback(async () => {
    if (!roleId) {
      setData(null);
      setMessage('');
      return;
    }
    setLoading(true);
    setError(null);
    setMessage('');
    try {
      const response = await roleService.getScopeAssignments(roleId);
      if (response.success) {
        setData(response.data ?? null);
      } else {
        setData(null);
        setMessage(response.message || 'No scope assignments available.');
      }
    } catch (err: any) {
      setError(err as Error);
      setData(null);
      setMessage(err?.response?.data?.message || 'Scope assignments are unavailable for this role.');
    } finally {
      setLoading(false);
    }
  }, [roleId]);

  useEffect(() => {
    fetchAssignments();
  }, [fetchAssignments]);

  return { data, loading, error, message, refetch: fetchAssignments };
};

export const useUpdateRoleScopeDefault = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const updateScopeDefault = useCallback(
    async (roleId: ID, data: UpdateRoleScopeDefaultRequest, options?: MutationOptions<RoleScopeAssignmentResponse | undefined>) => {
      setLoading(true);
      setError(null);
      try {
        const response = await roleService.updateScopeDefault(roleId, data);
        if (!response.success) throw new Error(response.message || 'Failed to update scope default');
        options?.onSuccess?.(response.data);
        return response.data;
      } catch (err) {
        const error = err as Error;
        setError(error);
        options?.onError?.(error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { updateScopeDefault, loading, error };
};

export const useRemoveRoleScopeAssignments = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const removeScopeAssignments = useCallback(async (roleId: ID, options?: MutationOptions) => {
    setLoading(true);
    setError(null);
    try {
      const response = await roleService.removeScopeAssignments(roleId);
      if (!response.success) throw new Error(response.message || 'Failed to remove scope assignments');
      options?.onSuccess?.();
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  return { removeScopeAssignments, loading, error };
};
